import axios from 'axios';

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.log('API Client Error:', error);
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email, password) {
    const response = await this.client.post('/api/auth/login', {
      email,
      password,
    });
    return response.data;
  }

  async getProfile() {
    const response = await this.client.get('/api/auth/profile');
    console.log('Raw API response:', response);
    console.log('Response data:', response.data);
    return response.data;
  }

  async updateProfile(data) {
    const response = await this.client.put('/api/auth/profile', data);
    return response.data;
  }

  async uploadDesignerLogo(file) {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await this.client.post('/api/auth/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteDesignerLogo() {
    const response = await this.client.delete('/api/auth/logo');
    return response.data;
  }

  // Admin endpoints
  async getUsers(params = {}) {
    const response = await this.client.get('/api/users', { params });
    return response.data;
  }

  async getUser(id) {
    const response = await this.client.get(`/api/users/${id}`);
    return response.data;
  }

  async createUser(data) {
    const response = await this.client.post('/api/users', data);
    return response.data;
  }

  async updateUser(id, data) {
    const response = await this.client.put(`/api/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id) {
    const response = await this.client.delete(`/api/users/${id}`);
    return response.data;
  }

  // System stats
  async getSystemStats() {
    const response = await this.client.get('/api/users/stats/overview');
    return response.data;
  }

  // Platform earnings
  async getPlatformEarnings() {
    const response = await this.client.get('/api/stripe-connect/platform/earnings');
    return response.data;
  }

  // Funeral Director endpoints
  async getFuneralDirectors(params = {}) {
    const response = await this.client.get('/api/users/funeral-directors', { params });
    return response.data;
  }

  async createFuneralDirector(data) {
    const funeralDirectorData = {
      ...data,
      type: 'funeral_director'
    };
    const response = await this.client.post('/api/users', funeralDirectorData);
    return response.data;
  }

  // Designer endpoints
  async getDesigners(params = {}) {
    const response = await this.client.get('/api/users/designers', { params });
    return response.data;
  }

  async createDesigner(data) {
    const designerData = {
      ...data,
      type: 'designer',
      role: 'designer' // Add the role field that the backend expects
    };
    const response = await this.client.post('/api/users', designerData);
    return response.data;
  }

  async getDesigner(id) {
    const response = await this.client.get(`/api/users/${id}`);
    return response.data;
  }

  async updateDesigner(id, data) {
    const response = await this.client.put(`/api/users/${id}`, data);
    return response.data;
  }

  async sendDesignerInvite(id, data) {
    const response = await this.client.post(`/api/users/${id}/send-invite`, data);
    return response.data;
  }

  async sendFuneralDirectorInvite(id, data) {
    const response = await this.client.post(`/api/users/${id}/send-invite`, {
      ...data,
      type: 'funeral_director'
    });
    return response.data;
  }

  async sendStaffInvite(id, data) {
    const response = await this.client.post(`/api/users/${id}/send-invite`, {
      ...data,
      type: 'staff'
    });
    return response.data;
  }

  async validateInviteToken(token) {
    const response = await this.client.get(`/api/users/validate-invite/${token}`);
    return response.data;
  }

  async resetPassword(token, password) {
    const response = await this.client.post('/api/users/reset', { token, password });
    return response.data;
  }

  async forgotPassword(email) {
    const response = await this.client.post('/api/users/forgot-password', { email });
    return response.data;
  }

  // Card endpoints
  async getCards(params = {}) {
    const response = await this.client.get('/api/cards', { params });
    return response.data;
  }

  async getCardStats(designerId = null) {
    const params = designerId ? { designerId } : {};
    const response = await this.client.get('/api/cards/stats', { params });
    return response.data;
  }

  async getCard(id) {
    const response = await this.client.get(`/api/cards/${id}`);
    return response.data;
  }

  async createCard(data) {
    const response = await this.client.post('/api/cards', data);
    return response.data;
  }

  async updateCard(id, data) {
    const response = await this.client.put(`/api/cards/${id}`, data);
    return response.data;
  }

  async deleteCard(id) {
    const response = await this.client.delete(`/api/cards/${id}`);
    return response.data;
  }

  async getCardImages(cardId) {
    const response = await this.client.get(`/api/cards/${cardId}/images`);
    return response.data;
  }

  async uploadCardImage(cardId, file, imageType = null) {
    // Use pre-signed URL upload for files to bypass API Gateway 10MB limit
    try {
      // Step 1: Get pre-signed URL
      const uploadUrlResponse = await this.client.post(`/api/cards/${cardId}/images/upload-url`, {
        mimeType: file.type,
        filename: file.name,
      });
      
      if (!uploadUrlResponse.data.success || !uploadUrlResponse.data.data.uploadUrl) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, imageId } = uploadUrlResponse.data.data;

      // Step 2: Upload directly to S3 using pre-signed URL
      // Convert File to ArrayBuffer to avoid browser automatically adding Content-Type header
      // ArrayBuffer has no MIME type, so browser won't send Content-Type header
      // This makes the upload more permissive - no Content-Type signature requirement
      const fileBuffer = await file.arrayBuffer();
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        body: fileBuffer,
        // Don't send Content-Type header - pre-signed URL doesn't require it
      });

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.statusText}`);
      }

      // Step 3: Notify backend that upload is complete
      const completePayload = {
        imageId: imageId,
        originalName: file.name,
        mimeType: file.type,
      };
      
      // Add imageType if provided
      if (imageType) {
        completePayload.imageType = imageType;
      }
      
      const completeResponse = await this.client.post(`/api/cards/${cardId}/images/complete`, completePayload);

      return completeResponse.data;
    } catch (error) {
      console.error('Pre-signed URL upload failed:', error);
      // Don't fallback - log the error and throw it
      // The pre-signed URL flow should always work in production
      throw error;
    }
  }

  async deleteCardImage(cardId, imageId) {
    const response = await this.client.delete(`/api/cards/${cardId}/images/${imageId}`);
    return response.data;
  }

  // Funeral Company endpoints
  async getCompanies(params = {}) {
    const response = await this.client.get('/api/companies', { params });
    return response.data;
  }

  async getCompany(id) {
    const response = await this.client.get(`/api/companies/${id}`);
    return response.data;
  }

  async createCompany(data) {
    const response = await this.client.post('/api/companies', data);
    return response.data;
  }

  async updateCompany(id, data) {
    const response = await this.client.put(`/api/companies/${id}`, data);
    return response.data;
  }

  async deleteCompany(id) {
    const response = await this.client.delete(`/api/companies/${id}`);
    return response.data;
  }

  // Legacy method names for backward compatibility (deprecated)
  async getFuneralCompanies(params = {}) {
    return this.getCompanies(params);
  }

  async getFuneralCompany(id) {
    return this.getCompany(id);
  }

  async createFuneralCompany(data) {
    return this.createCompany(data);
  }

  async updateFuneralCompany(id, data) {
    return this.updateCompany(id, data);
  }

  async deleteFuneralCompany(id) {
    return this.deleteCompany(id);
  }

  // Obituary endpoints
  async getObituaries(params = {}) {
    const response = await this.client.get('/api/obituaries', { params });
    return response.data;
  }

  async getPublicObituaries(params = {}) {
    const response = await this.client.get('/api/obituaries/public', { params });
    return response.data;
  }

  async getObituary(id) {
    const response = await this.client.get(`/api/obituaries/${id}`);
    return response.data;
  }

  async createObituary(data) {
    const response = await this.client.post('/api/obituaries', data);
    return response.data;
  }

  async updateObituary(id, data) {
    const response = await this.client.put(`/api/obituaries/${id}`, data);
    return response.data;
  }

  async deleteObituary(id) {
    const response = await this.client.delete(`/api/obituaries/${id}`);
    return response.data;
  }

  async getObituaryComments(id) {
    const response = await this.client.get(`/api/obituaries/public/${id}/comments`);
    return response.data;
  }

  async addObituaryComment(id, data) {
    const response = await this.client.post(`/api/obituaries/public/${id}/comments`, data);
    return response.data;
  }

  async sendSMSToFamily(id, data) {
    const response = await this.client.post(`/api/obituaries/${id}/send-sms`, data);
    return response.data;
  }

  async uploadObituaryPhoto(obituaryId, file) {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await this.client.post(`/api/obituaries/${obituaryId}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  getObituaryPhotoUrl(obituaryId) {
    const baseURL = this.client.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return `${baseURL}/api/obituaries/${obituaryId}/photo`;
  }

  async deleteObituaryPhoto(obituaryId) {
    const response = await this.client.delete(`/api/obituaries/${obituaryId}/photo`);
    return response.data;
  }

  // Branch endpoints
  async getBranches(params = {}) {
    const response = await this.client.get('/api/branches', { params });
    return response.data;
  }

  async getBranch(id) {
    const response = await this.client.get(`/api/branches/${id}`);
    return response.data;
  }

  async createBranch(data) {
    const response = await this.client.post('/api/branches', data);
    return response.data;
  }

  async updateBranch(id, data) {
    const response = await this.client.put(`/api/branches/${id}`, data);
    return response.data;
  }

  async deleteBranch(id) {
    const response = await this.client.delete(`/api/branches/${id}`);
    return response.data;
  }

  async uploadBranchLogo(branchId, file) {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await this.client.post(`/api/branches/${branchId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  getBranchLogoUrl(branchId) {
    return `${this.client.defaults.baseURL}/api/branches/${branchId}/logo`;
  }

  async deleteBranchLogo(branchId) {
    const response = await this.client.delete(`/api/branches/${branchId}/logo`);
    return response.data;
  }

  // Subscription endpoints
  async createSubscription(data) {
    const response = await this.client.post('/api/subscriptions', data);
    return response.data;
  }

  async updateSubscription(data) {
    const response = await this.client.put('/api/subscriptions', data);
    return response.data;
  }

  // Payment methods and setup intent
  async createSetupIntent() {
    const response = await this.client.post('/api/users/me/setupIntent');
    return response.data;
  }

  async setDefaultPaymentMethod() {
    const response = await this.client.put('/api/users/me/paymentMethod');
    return response.data;
  }

  // Stripe Connect endpoints
  async getMyStripeConnectAccount() {
    try {
      const response = await this.client.get('/api/stripe-connect/accounts/me');
      // Backend returns { success: true, data: {...}, hasAccountId, error?, message }
      // Return the full response so UI can check for errors
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        console.log('No Stripe Connect account found for user (404)');
        return { success: true, data: null, hasAccountId: false };
      }
      console.error('Error getting my Stripe Connect account:', error);
      throw error;
    }
  }

  async getStripeConnectAccount(accountId) {
    const response = await this.client.get(`/api/stripe-connect/accounts/${accountId}`);
    return response.data;
  }

  async createStripeConnectAccount(accountData) {
    const response = await this.client.post('/api/stripe-connect/accounts', accountData);
    return response.data;
  }

  async connectStripeAccount(connectData) {
    const response = await this.client.post('/api/stripe-connect/accounts/connect', connectData);
    return response.data;
  }

  async createAccountSession(accountId, components) {
    const response = await this.client.post(`/api/stripe-connect/accounts/${accountId}/sessions`, { components });
    return response.data;
  }

  async createLoginLink(accountId) {
    const response = await this.client.post(`/api/stripe-connect/accounts/${accountId}/login-links`);
    return response.data;
  }

  // Terms and Conditions - uses updateProfile endpoint
  async acceptTerms(termsType, version) {
    const acceptedAt = new Date().toISOString();
    const acceptedTerms = {
      [termsType]: {
        version,
        acceptedAt,
      },
    };
    const response = await this.client.put('/api/auth/profile', {
      acceptedTerms,
    });
    return response.data;
  }

  // Admin utilities — rebuild thumbnails + 5×7 preview assets from full-size uploads
  async regenerateCardImages(params = {}) {
    const body = params && typeof params === 'object' ? params : {};
    const response = await this.client.post('/api/admin/regenerate-images', body);
    return response.data;
  }

  async getImageRegenerationJob(jobId) {
    const response = await this.client.get(`/api/admin/regenerate-images/jobs/${jobId}`);
    return response.data;
  }

  /** @deprecated Use regenerateCardImages */
  async regenerateThumbnails(params = {}) {
    return this.regenerateCardImages(params);
  }

  // Admin taxonomy + categorization management (BR-CAT-09) — admin-only endpoints
  async getAdminTaxonomy() {
    const response = await this.client.get('/api/admin/taxonomy');
    return response.data;
  }

  async createTaxonomyCategory(data) {
    const response = await this.client.post('/api/admin/taxonomy', data);
    return response.data;
  }

  async updateTaxonomyCategory(id, data) {
    const response = await this.client.put(`/api/admin/taxonomy/${id}`, data);
    return response.data;
  }

  async retireTaxonomyCategory(id) {
    const response = await this.client.post(`/api/admin/taxonomy/${id}/retire`);
    return response.data;
  }

  async runCategorization(params = {}) {
    const body = params && typeof params === 'object' ? params : {};
    const response = await this.client.post('/api/admin/categorize', body);
    return response.data;
  }

  async getCategorizationStats() {
    const response = await this.client.get('/api/admin/categorize/stats');
    return response.data;
  }

  async getCardCategories(cardId) {
    const response = await this.client.get(`/api/admin/cards/${cardId}/categories`);
    return response.data;
  }

  // Orders endpoints
  async getOrders(params = {}) {
    const response = await this.client.get('/api/orders', { params });
    return response.data;
  }

  async getOrder(paymentIntentId) {
    const response = await this.client.get(`/api/orders/${paymentIntentId}`);
    return response.data;
  }

  // Transfers endpoints
  async getTransfers(params = {}) {
    const response = await this.client.get('/api/transfers', { params });
    return response.data;
  }

  async getTransferReconciliation(paymentId) {
    const response = await this.client.get(`/api/transfers/${paymentId}/reconcile`);
    return response.data;
  }

  async retryFailedTransfers(paymentId, data) {
    const response = await this.client.post(`/api/transfers/${paymentId}/retry`, data);
    return response.data;
  }

  // Beta issue reports (report an issue / feedback)
  async submitIssueReport(description) {
    const response = await this.client.post('/api/issue-reports', { description });
    return response.data;
  }

  async getIssueReports() {
    const response = await this.client.get('/api/issue-reports');
    return response.data;
  }

  async getIssueReport(id) {
    const response = await this.client.get(`/api/issue-reports/${id}`);
    return response.data;
  }

  async updateIssueReport(id, data) {
    const response = await this.client.patch(`/api/issue-reports/${id}`, data);
    return response.data;
  }

  async deleteIssueReport(id) {
    const response = await this.client.delete(`/api/issue-reports/${id}`);
    return response.data;
  }
}

// Create and export a singleton instance
const apiClient = new ApiClient();
export default apiClient;
